import React from 'react';

function isColorDark(hex) {
  const c = (hex || '#29D880').replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

// Hex → rgb string for rgba()
function hexToRgb(hex) {
  const c = (hex || '#29D880').replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return `${r},${g},${b}`;
}

const WebPreview = ({ data }) => {
  const {
    nombre    = 'Tu negocio',
    rubro     = '',
    descripcion = '',
    servicios = [],
    color: rawColor = '',
    logo      = null,
    heroImage = null,
    contacto  = '',
    telefono  = '',
    direccion = '',
    ubicacion = '',
    tagline   = '',
  } = data;

  const color   = rawColor || '#29D880';
  const rgb     = hexToRgb(color);
  const dark    = isColorDark(color);
  const onColor = dark ? '#ffffff' : '#080808';
  const inicial = (nombre || 'G').charAt(0).toUpperCase();

  const validServicios = servicios.filter(s => {
    const t = typeof s === 'string' ? s : (s?.title ?? '');
    return t.trim().length > 0;
  });

  // ── Styles ────────────────────────────────────────────────────────────────

  const S = {
    wrap: {
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      fontSize: 13,
      background: '#fafafa',
      color: '#111',
      lineHeight: 1.5,
      overflowY: 'auto',
      height: '100%',
      width: '100%',
    },

    // NAV
    nav: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 20px',
      background: '#0a0a0a',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      position: 'sticky',
      top: 0,
      zIndex: 20,
    },
    navBrand: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    navLogoBox: {
      width: 28,
      height: 28,
      borderRadius: 7,
      background: `rgba(${rgb},0.18)`,
      border: `1.5px solid rgba(${rgb},0.45)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 13,
      fontWeight: 800,
      color: color,
      flexShrink: 0,
    },
    navName: {
      fontWeight: 700,
      fontSize: 13,
      color: '#ffffff',
      letterSpacing: -0.2,
    },
    navLinks: {
      display: 'flex',
      gap: 16,
      fontSize: 11,
      color: 'rgba(255,255,255,0.3)',
    },
    navCta: {
      padding: '6px 14px',
      borderRadius: 99,
      background: color,
      color: onColor,
      fontSize: 11,
      fontWeight: 700,
      border: 'none',
      cursor: 'default',
      flexShrink: 0,
    },

    // HERO
    hero: {
      background: '#0d0d0d',
      padding: '40px 24px 36px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    heroGlow: {
      position: 'absolute',
      top: -60,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 320,
      height: 240,
      borderRadius: '50%',
      background: `radial-gradient(circle, rgba(${rgb},0.28) 0%, transparent 70%)`,
      pointerEvents: 'none',
    },
    heroDots: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `radial-gradient(circle, rgba(${rgb},0.22) 1px, transparent 1px)`,
      backgroundSize: '22px 22px',
      opacity: 0.6,
      pointerEvents: 'none',
    },
    heroContent: {
      position: 'relative',
      zIndex: 1,
    },
    heroLogoImg: {
      height: 52,
      margin: '0 auto 16px',
      objectFit: 'contain',
      display: 'block',
      filter: 'drop-shadow(0 0 16px rgba(0,0,0,0.5))',
    },
    heroMark: {
      width: 64,
      height: 64,
      borderRadius: 16,
      background: `rgba(${rgb},0.14)`,
      border: `2px solid rgba(${rgb},0.4)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
      fontSize: 26,
      fontWeight: 900,
      color: color,
      boxShadow: `0 0 32px rgba(${rgb},0.25)`,
    },
    heroBadge: {
      display: 'inline-block',
      padding: '3px 12px',
      borderRadius: 99,
      background: `rgba(${rgb},0.15)`,
      border: `1px solid rgba(${rgb},0.35)`,
      color: color,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      marginBottom: 14,
    },
    heroH1: {
      fontSize: 26,
      fontWeight: 800,
      color: '#ffffff',
      lineHeight: 1.2,
      letterSpacing: -0.5,
      margin: '0 0 10px',
    },
    heroDesc: {
      color: 'rgba(255,255,255,0.45)',
      fontSize: 12,
      lineHeight: 1.65,
      maxWidth: 300,
      margin: '0 auto 22px',
    },
    heroCta: {
      padding: '10px 26px',
      borderRadius: 99,
      background: color,
      color: onColor,
      fontSize: 13,
      fontWeight: 700,
      border: 'none',
      cursor: 'default',
      boxShadow: `0 0 28px rgba(${rgb},0.55)`,
      display: 'inline-block',
    },
    heroLocation: {
      color: 'rgba(255,255,255,0.2)',
      fontSize: 10,
      marginTop: 12,
    },

    // INFO STRIP
    infoStrip: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: 0,
      background: '#111111',
      borderTop: '1px solid rgba(255,255,255,0.04)',
      borderBottom: '1px solid rgba(255,255,255,0.04)',
      padding: '10px 20px',
    },
    infoItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      padding: '3px 12px',
    },
    infoDot: {
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: color,
      flexShrink: 0,
    },
    infoText: {
      fontSize: 11,
      color: 'rgba(255,255,255,0.45)',
      fontWeight: 500,
    },
    infoDivider: {
      width: 1,
      height: 14,
      background: 'rgba(255,255,255,0.08)',
      flexShrink: 0,
    },

    // SERVICES
    services: {
      background: '#ffffff',
      padding: '28px 20px',
    },
    servicesLabel: {
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: '0.18em',
      color: '#bbb',
      textTransform: 'uppercase',
      marginBottom: 14,
    },
    servicesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: 8,
    },
    serviceCard: {
      padding: '12px 12px 12px 14px',
      borderRadius: '0 10px 10px 0',
      background: '#fafafa',
      borderLeft: `3px solid ${color}`,
      borderTop: '1px solid #f0f0f0',
      borderRight: '1px solid #f0f0f0',
      borderBottom: '1px solid #f0f0f0',
    },
    serviceTitle: {
      fontWeight: 700,
      fontSize: 12,
      color: '#111',
      marginBottom: 3,
    },
    serviceDesc: {
      fontSize: 10,
      color: '#888',
      lineHeight: 1.5,
    },

    // QUOTE / COMMUNITY
    quote: {
      background: '#f6f5f3',
      padding: '24px 20px',
      borderTop: '1px solid #ebebeb',
      borderBottom: '1px solid #ebebeb',
    },
    quoteInner: {
      borderLeft: `3px solid rgba(${rgb},0.5)`,
      paddingLeft: 14,
    },
    quoteLabel: {
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      color: '#bbb',
      marginBottom: 8,
    },
    quoteText: {
      fontSize: 13,
      fontStyle: 'italic',
      color: '#444',
      lineHeight: 1.65,
      marginBottom: 12,
    },
    quoteAuthor: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    quoteAvatar: {
      width: 26,
      height: 26,
      borderRadius: '50%',
      background: `rgba(${rgb},0.2)`,
      border: `1.5px solid rgba(${rgb},0.4)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 11,
      fontWeight: 800,
      color: color,
      flexShrink: 0,
    },
    quoteAuthorName: {
      fontSize: 11,
      fontWeight: 700,
      color: '#333',
    },
    quoteAuthorRubro: {
      fontSize: 10,
      color: '#aaa',
    },

    // CONTACT
    contact: {
      background: color,
      padding: '32px 20px',
      textAlign: 'center',
    },
    contactH2: {
      fontSize: 22,
      fontWeight: 900,
      color: onColor,
      letterSpacing: -0.5,
      marginBottom: 6,
    },
    contactSub: {
      fontSize: 12,
      color: dark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)',
      marginBottom: 18,
      lineHeight: 1.5,
    },
    contactInfo: {
      marginBottom: 18,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 4,
    },
    contactLine: {
      fontSize: 11,
      color: dark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.65)',
    },
    contactBtn: {
      padding: '10px 28px',
      borderRadius: 99,
      border: `2px solid ${dark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)'}`,
      color: onColor,
      background: 'transparent',
      fontSize: 13,
      fontWeight: 700,
      cursor: 'default',
    },

    // FOOTER
    footer: {
      padding: '12px 20px',
      textAlign: 'center',
      background: '#080808',
      borderTop: '1px solid rgba(255,255,255,0.04)',
    },
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={S.wrap}>

      {/* ── NAV ── */}
      <nav style={S.nav}>
        <div style={S.navBrand}>
          {logo
            ? <img src={logo} alt="logo" style={{ height: 26, objectFit: 'contain', display: 'block' }} />
            : <div style={S.navLogoBox}>{inicial}</div>
          }
          <span style={S.navName}>{nombre}</span>
        </div>
        <div style={S.navLinks}>
          <span>Servicios</span>
          <span>Sobre</span>
          <span>Contacto</span>
        </div>
        <button style={S.navCta}>Contactar</button>
      </nav>

      {/* ── HERO ── */}
      <section style={S.hero}>
        {heroImage && (
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.22,
          }} />
        )}
        <div style={S.heroDots} />
        <div style={S.heroGlow} />
        <div style={S.heroContent}>
          {logo
            ? <img src={logo} alt={nombre} style={S.heroLogoImg} />
            : <div style={S.heroMark}>{inicial}</div>
          }
          {rubro && <div style={S.heroBadge}>{rubro}</div>}
          <h1 style={S.heroH1}>{tagline || nombre}</h1>
          {descripcion && (
            <p style={S.heroDesc}>{descripcion}</p>
          )}
          <button style={S.heroCta}>Hablemos →</button>
          {ubicacion && <p style={S.heroLocation}>📍 {ubicacion}</p>}
        </div>
      </section>

      {/* ── INFO STRIP — datos reales del negocio ── */}
      {(rubro || ubicacion || nombre) && (
        <div style={S.infoStrip}>
          {nombre && (
            <div style={S.infoItem}>
              <div style={S.infoDot} />
              <span style={S.infoText}>{nombre}</span>
            </div>
          )}
          {rubro && nombre && <div style={S.infoDivider} />}
          {rubro && (
            <div style={S.infoItem}>
              <div style={S.infoDot} />
              <span style={S.infoText}>{rubro}</span>
            </div>
          )}
          {ubicacion && (rubro || nombre) && <div style={S.infoDivider} />}
          {ubicacion && (
            <div style={S.infoItem}>
              <div style={S.infoDot} />
              <span style={S.infoText}>{ubicacion}</span>
            </div>
          )}
        </div>
      )}

      {/* ── SERVICIOS ── */}
      {validServicios.length > 0 && (
        <section style={S.services}>
          <p style={S.servicesLabel}>Lo que hacemos</p>
          <div style={S.servicesGrid}>
            {validServicios.map((s, i) => {
              const title = typeof s === 'string' ? s : (s.title || '');
              const desc  = typeof s === 'string' ? '' : (s.desc  || '');
              return (
                <div key={i} style={S.serviceCard}>
                  <p style={S.serviceTitle}>{title}</p>
                  {desc && <p style={S.serviceDesc}>{desc}</p>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── SOBRE / COMMUNITY QUOTE ── */}
      <section style={S.quote}>
        <div style={S.quoteInner}>
          <p style={S.quoteLabel}>¿Quiénes somos?</p>
          <p style={S.quoteText}>
            "{descripcion || 'Hacemos lo que hacemos con dedicación y pasión — y eso se nota.'}"
          </p>
          <div style={S.quoteAuthor}>
            {logo
              ? <img src={logo} alt="logo" style={{ height: 26, width: 26, borderRadius: '50%', objectFit: 'contain', border: `1.5px solid rgba(${rgb},0.4)` }} />
              : <div style={S.quoteAvatar}>{inicial}</div>
            }
            <div>
              <p style={S.quoteAuthorName}>{nombre}</p>
              {rubro && <p style={S.quoteAuthorRubro}>{rubro}</p>}
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACTO ── */}
      <section style={S.contact}>
        <p style={S.contactH2}>¿Hablamos?</p>
        <p style={S.contactSub}>
          {contacto || telefono
            ? 'Escribinos cuando quieras.'
            : 'Completá tu info de contacto arriba.'
          }
        </p>
        <div style={S.contactInfo}>
          {contacto  && <p style={S.contactLine}>✉ {contacto}</p>}
          {telefono  && <p style={S.contactLine}>📱 {telefono}</p>}
          {direccion && <p style={S.contactLine}>📍 {direccion}</p>}
          {!contacto && !telefono && !direccion && (
            <p style={{ fontSize: 11, color: dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}>
              contacto · teléfono · dirección
            </p>
          )}
        </div>
        <button style={S.contactBtn}>Escribinos →</button>
      </section>

      {/* ── FOOTER ── */}
      <div style={S.footer}>
        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>Hecho con </span>
        <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: 700 }}>GuIA</span>
      </div>

    </div>
  );
};

export default WebPreview;
